import { Box, Paper, Stack, Typography, Alert, Link } from "@mui/material";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router";
import { useState } from "react";
import { FormField, LoadingButton, isValidCpf, isValidCnpj } from "@safrater/ui";
import { api } from "../../api/client";

interface Form {
  name: string;
  email: string;
  cpf: string;
  cnpj: string;
  institutionName?: string;
  institutionEmail?: string;
  institutionPhone?: string;
}

export function SignupPage() {
  const form = useForm<Form>({ defaultValues: { name: "", email: "", cpf: "", cnpj: "" } });
  const [done, setDone] = useState<{ isNew: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  if (done) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 480 }}>
          <Typography variant="h5" gutterBottom>Cadastro recebido</Typography>
          <Typography variant="body2">
            {done.isNew
              ? "Sua instituição foi cadastrada e aguarda aprovação do administrador. Você receberá um email assim que for liberada."
              : "Seu cadastro está aguardando aprovação. Você receberá um email assim que for liberado."}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Link component={RouterLink} to="/login">Ir para o login</Link>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 560, width: "100%" }}>
        <Typography variant="h5" gutterBottom>Cadastrar minha instituição</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Informe seu CNPJ. Se já estivermos cadastrando a instituição, faremos seu vínculo automaticamente.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack
          component="form"
          spacing={2}
          onSubmit={form.handleSubmit(async (data) => {
            setError(null);
            try {
              const res = await api.post<{ isNewInstitution: boolean }>("/signup", data);
              setDone({ isNew: res.data.isNewInstitution });
            } catch (e: unknown) {
              const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
              setError(msg ?? "Falha no cadastro. Verifique os dados.");
            }
          })}
        >
          <FormField label="Nome completo" {...form.register("name", { required: "Obrigatório" })} errorMessage={form.formState.errors.name?.message} />
          <FormField label="Email" type="email" {...form.register("email", { required: "Obrigatório" })} errorMessage={form.formState.errors.email?.message} />
          <FormField label="CPF" {...form.register("cpf", { required: "Obrigatório", validate: (v) => isValidCpf(v) || "CPF inválido" })} errorMessage={form.formState.errors.cpf?.message} />
          <FormField label="CNPJ da instituição" {...form.register("cnpj", { required: "Obrigatório", validate: (v) => isValidCnpj(v) || "CNPJ inválido" })} errorMessage={form.formState.errors.cnpj?.message} />
          <Typography variant="caption" color="text.secondary">Se a instituição ainda não está cadastrada, informe os dados abaixo:</Typography>
          <FormField label="Nome da instituição" {...form.register("institutionName")} />
          <FormField label="Email da instituição" type="email" {...form.register("institutionEmail")} />
          <FormField label="Telefone" {...form.register("institutionPhone")} />
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Link component="button" type="button" onClick={() => navigate("/login")}>Voltar para o login</Link>
            <LoadingButton type="submit" variant="contained" loading={form.formState.isSubmitting}>Enviar</LoadingButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
